import { useCallback, useState } from 'react';
import { getAutocompletePassenger, getPassengerId } from 'features/salesOrder/services';
import { IPassenger, IPassengerAutocomplete, PassengerDocumentEnum } from 'features/salesOrder/types';

export interface PassengerSearchMatch {
	passengerId: string;
	name: string;
	lastName: string;
	birthdate?: string;
	documents: { documentType: PassengerDocumentEnum; documentNumber: string }[];
}

const normalizeDocumentNumber = (value: string): string => value.replace(/\s/g, '').toLowerCase();

export interface UsePassengerByDocumentResult {
	loading: boolean;
	error: string | null;
	matches: PassengerSearchMatch[];
	searchByDocument: (documentNumber: string, documentType?: PassengerDocumentEnum) => Promise<PassengerSearchMatch[]>;
	loadPassengerDetail: (passengerId: string) => Promise<IPassenger | null>;
	reset: () => void;
}

export const usePassengerByDocument = (): UsePassengerByDocumentResult => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [matches, setMatches] = useState<PassengerSearchMatch[]>([]);

	const searchByDocument = useCallback(async (documentNumber: string, documentType?: PassengerDocumentEnum) => {
		const trimmed = documentNumber.trim();
		setError(null);
		setMatches([]);

		if (trimmed.length < 4) {
			setError('Ingrese al menos 4 caracteres del número de documento');
			return [];
		}

		setLoading(true);
		try {
			const { data } = await getAutocompletePassenger(trimmed);
			const normalizedQuery = normalizeDocumentNumber(trimmed);

			const filtered = (data as IPassengerAutocomplete[])
				.filter((p) => p.isPhysicalPerson)
				.filter((p) =>
					(p.documents || []).some((doc) => {
						const numberMatch = normalizeDocumentNumber(doc.documentNumber).includes(normalizedQuery);
						const typeMatch = documentType ? doc.documentType === documentType : true;
						return numberMatch && typeMatch;
					}),
				)
				.map((p) => ({
					passengerId: p.passengerId,
					name: p.name,
					lastName: p.lastName,
					birthdate: p.birthdate,
					documents: p.documents || [],
				}));

			setMatches(filtered);
			if (filtered.length === 0) {
				setError(null);
			}
			return filtered;
		} catch {
			setError('Error al buscar pasajeros. Intentá de nuevo.');
			return [];
		} finally {
			setLoading(false);
		}
	}, []);

	const loadPassengerDetail = useCallback(async (passengerId: string) => {
		setLoading(true);
		try {
			const { data } = await getPassengerId(passengerId);
			return data;
		} catch {
			setError('No se pudo cargar el pasajero seleccionado');
			return null;
		} finally {
			setLoading(false);
		}
	}, []);

	const reset = useCallback(() => {
		setMatches([]);
		setError(null);
	}, []);

	return {
		loading,
		error,
		matches,
		searchByDocument,
		loadPassengerDetail,
		reset,
	};
};
